import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  input,
  model,
  output,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NeoCheckboxV2Module } from '@neogov/pattern-library/neo-checkbox-v2';

@Component({
  selector: 'cm-checkbox',
  standalone: true,
  imports: [CommonModule, NeoCheckboxV2Module],
  templateUrl: './checkbox.component.html',
  styleUrl: './checkbox.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckboxComponent),
      multi: true,
    },
  ],
})
export class CheckboxComponent implements ControlValueAccessor {
  private readonly _cdr = inject(ChangeDetectorRef);

  public readonly checked = model<boolean>(false);
  public readonly disabled = input<boolean>(false);
  public readonly label = input<string>('');
  public readonly ariaLabel = input<string>('');
  public readonly hint = input<string>('');
  public readonly indeterminate = input<boolean>(false);

  public readonly checkedChange = output<boolean>();

  // Internal state for disabled (separate from form control)
  public isDisabled = false;

  // ControlValueAccessor callbacks
  private _onChange: (value: boolean) => void = () => {
    /* Initialized by Angular forms */
  };
  private _onTouched: () => void = () => {
    /* Initialized by Angular forms */
  };

  /**
   * Gets the effective checked state.
   * Uses checked() model as single source of truth.
   */
  public get effectiveChecked(): boolean {
    return this.checked();
  }

  /**
   * Gets the effective disabled state.
   * Combines form control disabled state with input binding.
   */
  public get effectiveDisabled(): boolean {
    return this.isDisabled || this.disabled();
  }

  public onCheckboxChange(checked: boolean): void {
    this.checked.set(checked);
    this._onChange(checked);
    this._onTouched();
    this.checkedChange.emit(checked);
  }

  // ControlValueAccessor implementation
  public writeValue(value: boolean | null): void {
    this.checked.set(value ?? false);
    this._cdr.markForCheck();
  }

  public registerOnChange(fn: (value: boolean) => void): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this._cdr.markForCheck();
  }
}
